import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, CircleNotch, X, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

const LANGS = { fr: "Francês", pt: "Português", en: "Inglês" };

function Themes({ value, onChange, defaults }) {
  const [draft, setDraft] = useState("");
  const add = (t) => { const v = t.trim(); if (v && !value.includes(v)) onChange([...value, v]); setDraft(""); };
  const missing = defaults.filter((d) => !value.includes(d));
  return (
    <div className="space-y-3" data-testid="mk-settings-themes">
      <div className="flex flex-wrap gap-2">
        {value.map((t, i) => (
          <span key={t} className="flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-sm text-primary-foreground" data-testid={`mk-theme-${i}`}>
            {t}
            <button type="button" onClick={() => onChange(value.filter((x) => x !== t))} data-testid={`mk-theme-remove-${i}`}><X size={12} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Novo tema (ex.: Conseil : joints des pavés)" maxLength={80}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(draft); } }} data-testid="mk-theme-input" />
        <Button type="button" variant="outline" onClick={() => add(draft)} data-testid="mk-theme-add"><Plus size={16} /></Button>
      </div>
      {missing.length > 0 && (
        <div className="flex flex-wrap gap-1.5 text-xs">
          <span className="text-muted-foreground">Sugestões:</span>
          {missing.map((d) => <button key={d} type="button" onClick={() => add(d)} className="rounded-full border border-border px-2 py-0.5 hover:bg-secondary">+ {d}</button>)}
        </div>
      )}
      <p className="text-xs text-muted-foreground">Os temas rodam de semana para semana. Temas com “Conseil”/“Dica” geram um visual de texto sobre o fundo da marca.</p>
    </div>
  );
}

export default function MarketingSettings() {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/marketing/settings").then((r) => setF(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!f) return <div className="flex h-60 items-center justify-center"><CircleNotch size={26} className="animate-spin text-primary" /></div>;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const { default_themes, ...payload } = f;
      const { data } = await api.put("/marketing/settings", { ...payload, posts_per_week: Number(f.posts_per_week), stories_per_week: Number(f.stories_per_week) });
      setF(data);
      toast.success("Configurações guardadas");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="marketing-settings-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" className="rounded-full" asChild><Link to="/marketing"><ArrowLeft size={18} /></Link></Button>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Marketing</p>
            <h1 className="font-display text-3xl font-extrabold tracking-tight">Configurações de marketing</h1>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" asChild><Link to="/marketing/marca">Logo, cores, telefone e site</Link></Button>
          <Button className="rounded-full" onClick={save} disabled={busy} data-testid="mk-settings-save">{busy ? <CircleNotch className="animate-spin" /> : "Guardar"}</Button>
        </div>
      </div>

      <section className="grid gap-4 rounded-xl border border-border bg-card p-5 md:grid-cols-2">
        <label className="flex items-center gap-3 md:col-span-2">
          <Switch checked={f.auto_generate} onCheckedChange={(v) => setF({ ...f, auto_generate: v })} data-testid="mk-settings-auto" />
          <span className="text-sm"><b>Geração automática</b> todas as segundas-feiras às 7h (hora suíça) — reescreve os itens em Rascunho</span>
        </label>
        <div className="space-y-1.5"><Label>Idioma dos textos</Label>
          <Select value={f.language} onValueChange={(v) => setF({ ...f, language: v })}>
            <SelectTrigger data-testid="mk-settings-language"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(LANGS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-1.5"><Label>Público / região</Label><Input value={f.audience} onChange={set("audience")} data-testid="mk-settings-audience" /></div>
        <div className="space-y-1.5"><Label>Publicações (feed) por semana</Label><Input type="number" min="0" max="7" value={f.posts_per_week} onChange={set("posts_per_week")} data-testid="mk-settings-posts" /></div>
        <div className="space-y-1.5"><Label>Stories por semana</Label><Input type="number" min="0" max="14" value={f.stories_per_week} onChange={set("stories_per_week")} data-testid="mk-settings-stories" /></div>
        <div className="space-y-1.5"><Label>Chamada para ação</Label><Input value={f.cta} onChange={set("cta")} data-testid="mk-settings-cta" /></div>
        <div className="space-y-1.5"><Label>Hashtags de base</Label><Input value={f.hashtags_seed} onChange={set("hashtags_seed")} data-testid="mk-settings-hashtags" /></div>
        <p className="text-xs text-muted-foreground md:col-span-2">O número de posts e stories aplica-se às semanas novas (as semanas já criadas mantêm os seus itens).</p>
      </section>

      <section className="space-y-3 rounded-xl border border-border bg-card p-5">
        <h2 className="font-display text-lg font-bold">Temas preferidos</h2>
        <Themes value={f.themes} onChange={(t) => setF({ ...f, themes: t })} defaults={f.default_themes || []} />
      </section>
    </div>
  );
}
